import React, { useState, useEffect } from "react";
import { Card, Grid, Icon, TextField } from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete } from "@mui/material";
import {
  autoCompleteProps,
  wrapCell,
} from "components/CommonComponent/CommonFunction";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DATE_RANGE_OPTIONS } from "components/common/constant";
import { getContractorList } from "actions/contractor";
import { getDepartmentList } from "actions/department";
import { getDesignationList } from "actions/designation";
import {
  getUsersSystemReportList,
  exportUsersSystemReportData,
} from "actions/reports";
import { usersSystemReportList } from "constants/reports";
import { useNotify } from "components/CommonComponent/NotificationProvider";

function ActiveUserSystemControlReport() {
  const dispatch = useDispatch();
  const [pageNum, setPageNum] = useState(1);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [showClearButton, setShowClearButton] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();

    const startDateString = startDate.toISOString().split("T")[0];
    const endDateString = endDate.toISOString().split("T")[0];

    return {
      dateRange: "TODAY",
      startDate: startDateString,
      endDate: endDateString,

      employeeName: "",
      employeeID: "",
      contractorName: "",
      department: "",
      departmentCode: "",
      designation: "",

      filterParams: JSON.stringify([
        {
          field: "Date",
          condition: "gte",
          filterValues: [startDateString],
        },
        {
          field: "Date",
          condition: "lte",
          filterValues: [endDateString],
        },
      ]),
    };
  });

  const { notify } = useNotify();
  const contractorListData = useSelector(
    (state) => state.contractor.contractorList,
  );
  const reportListData = useSelector(
    (state) => state.employeeActivityReport?.usersSystemReportList,
  );
  const departmentListData = useSelector(
    (state) => state.department.departmentList,
  );
  const designationListData = useSelector(
    (state) => state.designation.designationList,
  );

  useEffect(() => {
    if (reportListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: reportListData?.data?.usersSystemReport?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (reportListData?.code || reportListData?.message) {
      setListState({
        isLoading: false,
        error: reportListData?.message || "Failed to load users system report!",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [reportListData]);

  useEffect(() => {
    getUsersSystemReportList(dispatch, pageNum, filters.filterParams);

    return () => {
      dispatch({
        type: usersSystemReportList,
        payload: {},
      });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    getContractorList(dispatch);
    getDepartmentList(dispatch);
    getDesignationList(dispatch);
  }, [dispatch]);

  const getDateRange = (days) => {
    const today = new Date();

    if (days === "TODAY") {
      const todayString = dayjs(today).format("YYYY-MM-DD");

      return {
        startDate: todayString,
        endDate: todayString,
      };
    }

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - 1);

    if (days === "1") {
      const yesterdayString = dayjs(endDate).format("YYYY-MM-DD");

      return {
        startDate: yesterdayString,
        endDate: yesterdayString,
      };
    }

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (Number(days) - 1));

    return {
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
    };
  };

  const buildFilterParams = () => {
    const filterParams = [];

    filterParams.push({
      field: "Date",
      condition: "gte",
      filterValues: [filters.startDate],
    });

    filterParams.push({
      field: "Date",
      condition: "lte",
      filterValues: [filters.endDate],
    });

    if (filters.employeeName) {
      filterParams.push({
        field: "EmployeeName",
        condition: "like",
        filterValues: [filters.employeeName],
      });
    }

    if (filters.employeeID) {
      filterParams.push({
        field: "EmployeeID",
        condition: "like",
        filterValues: [filters.employeeID],
      });
    }

    if (filters.contractorName) {
      filterParams.push({
        field: "ContractorName",
        condition: "like",
        filterValues: [filters.contractorName],
      });
    }

    if (filters.department) {
      filterParams.push({
        field: "DepartmentName",
        condition: "like",
        filterValues: [filters.department],
      });
    }

    if (filters.departmentCode) {
      filterParams.push({
        field: "DepartmentCode",
        condition: "like",
        filterValues: [filters.departmentCode],
      });
    }

    if (filters.designation) {
      filterParams.push({
        field: "Designation",
        condition: "like",
        filterValues: [filters.designation],
      });
    }

    return JSON.stringify(filterParams);
  };

  const handleSearch = () => {
    const filterParams = buildFilterParams();

    if (filterParams === filters.filterParams) {
      return;
    }

    setListState((prev) => ({
      ...prev,
      isLoading: true,
      showLoaderOnClick: true,
    }));

    setFilters((prev) => ({
      ...prev,
      filterParams,
    }));

    setShowClearButton(true);

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const handleClear = () => {
    const today = dayjs().format("YYYY-MM-DD");

    setFilters({
      dateRange: "TODAY",
      startDate: today,
      endDate: today,

      employeeName: "",
      employeeID: "",
      contractorName: "",
      department: "",
      departmentCode: "",
      designation: "",

      filterParams: JSON.stringify([
        {
          field: "Date",
          condition: "gte",
          filterValues: [today],
        },
        {
          field: "Date",
          condition: "lte",
          filterValues: [today],
        },
      ]),
    });

    setShowClearButton(false);

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const tableData =
    reportListData?.data?.usersSystemReport?.map((item, index) => ({
      srNo: index + 1,
      date: item.date ? dayjs(item.date).format("DD MMM YYYY") : "-",
      employeeName: item.employeeName || "-",
      employeeId: item.employeeID || "-",
      contractorName: item.contractorName || "-",
      department: item.departmentName || "-",
      deptCode: item.departmentCode || "-",
      designation: item.designation || "-",
      role: item.role || "-",
      lastLoginDate: item.lastLoginDate
        ? dayjs(item.lastLoginDate).format("DD MMM YYYY")
        : "-",
      daysNotLogged: item.daysNotLogged ?? "-",
    })) || [];

  const columns = [
    {
      Header: "Sr No",
      accessor: "srNo",
      width: "100px",
    },
    {
      Header: "Date",
      accessor: "date",
      width: "120px",
      Cell: (cell) => wrapCell(cell.value || "-", "120px"),
    },
    {
      Header: "Employee Name",
      accessor: "employeeName",
      width: "190px",
      Cell: (cell) => wrapCell(cell.value || "-", "190px"),
    },
    {
      Header: "Employee ID",
      accessor: "employeeId",
      width: "165px",
      Cell: (cell) => wrapCell(cell.value || "-", "165px"),
    },
    {
      Header: "Contractor Name",
      accessor: "contractorName",
      width: "220px",
      Cell: (cell) => wrapCell(cell.value || "-", "220px"),
    },
    {
      Header: "Department",
      accessor: "department",
      width: "220px",
      Cell: (cell) => wrapCell(cell.value || "-", "220px"),
    },
    {
      Header: "Dept Code",
      accessor: "deptCode",
      width: "150px",
      Cell: (cell) => wrapCell(cell.value || "-", "150px"),
    },
    {
      Header: "Designation",
      accessor: "designation",
      width: "180px",
      Cell: (cell) => wrapCell(cell.value || "-", "180px"),
    },
    {
      Header: "Role",
      accessor: "role",
      width: "140px",
      Cell: (cell) => wrapCell(cell.value || "-", "140px"),
    },
    {
      Header: "Last Login Date",
      accessor: "lastLoginDate",
      width: "160px",
      Cell: (cell) => wrapCell(cell.value || "-", "160px"),
    },
    {
      Header: "Days Not Logged",
      accessor: "daysNotLogged",
      width: "170px",
      Cell: (cell) => wrapCell(cell.value || "-", "170px"),
    },
  ];

  const downloadUsersSystemReportCSV = async () => {
    try {
      setExportLoading(true);

      const res = await exportUsersSystemReportData(filters.filterParams);
      
      const url = window.URL.createObjectURL(res.data);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ActiveUser&SystemControlReport.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      notify(
        res?.message || "UsersSystemReport file downloaded successfully",
        "success",
      );
    } catch (error) {
      notify("Failed to download usersSystemReport file", "error");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              {/* Header */}
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Active User & System Control Report
                </MDTypography>
                <MDBox display="flex" gap={2}>
                  <MDButton
                    variant="gradient"
                    color="light"
                    startIcon={<Icon>file_upload</Icon>}
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      textTransform: "none",
                    }}
                    onClick={downloadUsersSystemReportCSV}
                  >
                    {exportLoading ? (
                      <>
                        <i className="fa fa-spinner fa-spin mr-2" />
                        Exporting...
                      </>
                    ) : (
                      "Export Report"
                    )}
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox px={3} pb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={DATE_RANGE_OPTIONS}
                      value={
                        DATE_RANGE_OPTIONS.find(
                          (item) => item.value === filters.dateRange,
                        ) || null
                      }
                      getOptionLabel={(option) => option.label}
                      onChange={(event, value) => {
                        if (!value) return;

                        if (value.value === "CUSTOM") {
                          setFilters({
                            ...filters,
                            dateRange: "CUSTOM",
                          });
                        } else {
                          const dates = getDateRange(value.value);

                          setFilters({
                            ...filters,
                            dateRange: value.value,
                            startDate: dates.startDate,
                            endDate: dates.endDate,
                          });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Date Range"
                        />
                      )}
                    />
                  </Grid>

                  {filters.dateRange === "CUSTOM" && (
                    <>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            label="Start Date"
                            value={dayjs(filters.startDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                startDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            label="End Date"
                            minDate={dayjs(filters.startDate)}
                            value={dayjs(filters.endDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                endDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by Name"
                      value={filters.employeeName}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          employeeName: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by Emp ID"
                      value={filters.employeeID}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          employeeID: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={contractorListData?.data?.Contractor || []}
                      getOptionLabel={(option) => option?.name || ""}
                      value={
                        contractorListData?.data?.Contractor?.find(
                          (item) => item.name === filters.contractorName,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          contractorName: newValue?.name || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Contractor Name"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={departmentListData?.data?.Department || []}
                      getOptionLabel={(option) => option?.name || ""}
                      value={
                        departmentListData?.data?.Department?.find(
                          (item) => item.name === filters.department,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          department: newValue?.name || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Department"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={departmentListData?.data?.Department || []}
                      getOptionLabel={(option) => option?.code || ""}
                      value={
                        departmentListData?.data?.Department?.find(
                          (item) => item.code === filters.departmentCode,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          departmentCode: newValue?.code || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Department Code"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={designationListData?.data?.Designation || []}
                      getOptionLabel={(option) => option?.name || ""}
                      value={
                        designationListData?.data?.Designation?.find(
                          (item) => item.name === filters.designation,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          designation: newValue?.name || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Designation"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg="auto">
                    <MDBox display="flex" gap={1}>
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {showClearButton && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Total Records */}
              <MDBox px={3} py={1}>
                <MDTypography variant="body1" color="deepBlue">
                  Total Records: {reportListData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>

              {/* Table */}
              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="error">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="text">
                      No records found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={tableData || []}
                    isSorted={true}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (reportListData?.data?.totalRecords || 0) /
                        (reportListData?.data?.noOfRecordsPerPage || 25),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default ActiveUserSystemControlReport;
